import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api/client';
import AddOrderPopup_SmallBags from './AddOrderPopup_SmallBags';
import UploadOrdersPopup_SmallBags from './UploadOrdersPopup';
import DemandDetailsPopup from './DemandDetailsPopup';
import ColumnFilterPopup from './ColumnFilterPopup';

const SmallBagsTab = ({ selectedPlanning, onRefresh, onRoundRef, setIsRounding, filters, currentView, isUploadOrdersOpen, onUploadOrdersClose, searchQuery = '', columnFilters = {}, setColumnFilters = () => {} }) => {
  const [tableData, setTableData] = useState([]);
  const [inventoryData, setInventoryData] = useState({});
  const [seedInventoryData, setSeedInventoryData] = useState({});
  const [editingCell, setEditingCell] = useState(null);
  const [months, setMonths] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [ordersData, setOrdersData] = useState({});
  const [statuses, setStatuses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [currentOrderData, setCurrentOrderData] = useState({ sku: '', year: 0, month_num: 0, packName: '' });
  const [isAddOrderOpen, setIsAddOrderOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [detailsData, setDetailsData] = useState({});

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const openDetailsPopup = (sbsku, year, month) => {
    setDetailsData({ sku: sbsku, year, month });
    setIsDetailsOpen(true);
  };

  const closeDetailsPopup = () => {
    setIsDetailsOpen(false);
  };

  // Compute orders by effective month (for calculations and display)
  const { ordersByEffectiveMonth, ordersByEffectiveMonthList } = useMemo(() => {
    const now = new Date();
    const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const totals = {};
    const lists = {};

    Object.entries(ordersData).forEach(([sku, orders]) => {
      orders.forEach(order => {
        let effectiveMonth;
        if (order.statusID === 'BO' && order.bo_follow_up_date) {
          const d = new Date(order.bo_follow_up_date);
          if (isNaN(d.getTime())) return;
          effectiveMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        } else if (order.est_del_date) {
          const d = new Date(order.est_del_date);
          if (isNaN(d.getTime())) return;
          effectiveMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        } else {
          return;
        }
        // Undelivered orders with a past delivery date roll into the current month
        // (requested by Vlada, 2026-07-08: order 042026 with est. delivery 26.06
        // disappeared from the report instead of moving to July)
        if (effectiveMonth < currentMonthKey) effectiveMonth = currentMonthKey;

        if (!totals[sku]) totals[sku] = {};
        if (!totals[sku][effectiveMonth]) totals[sku][effectiveMonth] = 0;
        totals[sku][effectiveMonth] += Number(order.qty) || 0;

        if (!lists[sku]) lists[sku] = {};
        if (!lists[sku][effectiveMonth]) lists[sku][effectiveMonth] = [];
        lists[sku][effectiveMonth].push(order);
      });
    });

    return { ordersByEffectiveMonth: totals, ordersByEffectiveMonthList: lists };
  }, [ordersData]);

  // Fetch statuses, suppliers, and seed inventory
  useEffect(() => {
    const fetchStatusesAndSuppliers = async () => {
      try {
        const [statusesRes, suppliersRes] = await Promise.all([
          api.get('/api/orders_statuses'),
          api.get('/api/materialsuppliers_for_type/1')
        ]);
        setStatuses(statusesRes.data);
        setSuppliers(suppliersRes.data);
      } catch (error) {
        console.error('Error fetching statuses or suppliers:', error);
      }
    };
    fetchStatusesAndSuppliers();
  }, []);

  // Fetch orders data
  const fetchOrdersDataWithParams = async (rows) => {
    const skus = rows.map(row => row.sbsku);
    if (skus.length === 0) return;
    try {
      const response = await api.post('/api/get_orders_for_tuples', { skus });
      const ordersMap = response.data.reduce((acc, order) => {
        if (!acc[order.sku]) acc[order.sku] = [];
        acc[order.sku].push(order);
        return acc;
      }, {});
      setOrdersData(ordersMap);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchOrdersData = async () => {
    await fetchOrdersDataWithParams(tableData);
  };

  

  // Popup management
  const openAddOrderPopup = (sbsku, year, month, packName, order = null) => {
    setCurrentOrderData({ 
      sku: sbsku, 
      year: parseInt(year), 
      month_num: parseInt(month),
      packName,
      initialData: order
    });
    setIsAddOrderOpen(true);
  };

  const closeAddOrderPopup = () => {
    setIsAddOrderOpen(false);
  };

  const handleOrderSave = async () => {
    closeAddOrderPopup();
    await fetchData();
    await fetchOrdersData();
  };

  // Fetch data
  const fetchData = async () => {
    if (!selectedPlanning) return;
    setIsLoading(true);
    try {
      const skus = tableData.map(row => row.sbsku);
      const [demandResponse, stockResponse, seedInventoryRes] = await Promise.all([
        api.get('/api/smallbags_forecast', {
          params: { forecast_id: selectedPlanning }
        }),
        api.get('/api/smallbagsinventory_stock'),
        api.get('/api/individualpacketsinventory_monthlyplan', {
          params: { limit: 1000, SKU: skus.join(',') }
        })
      ]);

      const inventoryMap = stockResponse.data.reduce((acc, item) => {
        acc[item.sku] = item.stock;
        return acc;
      }, {});
      setInventoryData(inventoryMap);

      const seedInventoryMap = seedInventoryRes.data.data.reduce((acc, item) => {
        acc[item.SKU] = item.currentQTY;
        return acc;
      }, {});
      setSeedInventoryData(seedInventoryMap);
      console.log('Fetched SKUs:', skus);
      console.log('seedInventoryData:', seedInventoryMap);

      const dataMap = new Map();
      const monthsSet = new Set();

      demandResponse.data.forEach(item => {
        if (!item.year || !item.month) return;

        const key = `${item.sbsku}-${item.packName}`;
        if (!dataMap.has(key)) {
          dataMap.set(key, {
            sbsku: item.sbsku,
            packName: item.packName,
            brandID: item.brandID,
            months: {}
          });
        }

        const monthKey = `${item.year}-${String(item.month).padStart(2, '0')}`;
        monthsSet.add(monthKey);
        dataMap.get(key).months[monthKey] = {
          to_produce: item.total_to_produce_quantity || 0,
          fact: item.total_fact_quantity || 0,
          plan: item.total_plan_quantity || 0
        };
      });

      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;

      let sortedMonths = [];
      for (let month = currentMonth; month <= 12; month++) {
        sortedMonths.push(`${currentYear}-${String(month).padStart(2, '0')}`);
      }
      for (let month = 1; month <= currentMonth; month++) {
        sortedMonths.push(`${currentYear + 1}-${String(month).padStart(2, '0')}`);
      }
      setMonths(sortedMonths);

      const [brandsResponse] = await Promise.all([
        api.get('/api/planning-brands')
      ]);
      const brandMap = brandsResponse.data.reduce((acc, brand) => {
        acc[brand.brandID] = brand.brandName;
        return acc;
      }, {});

      let filteredData = Array.from(dataMap.values()).map(row => ({
        ...row,
        brandName: brandMap[row.brandID] || 'Unknown'
      }));

      if (filters?.selectedBrands?.length > 0) {
        filteredData = filteredData.filter(row => filters.selectedBrands.includes(row.brandName));
      }

      setTableData(filteredData);
      // Передаём filteredData напрямую, т.к. state tableData ещё не обновился
      await fetchOrdersDataWithParams(filteredData);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (onRefresh) onRefresh(fetchData);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlanning]);

  // Calculate total packs
  const calculateTotalPacks = (type, monthKey) => {
    return tableData.reduce((sum, row) => {
      const data = row.months[monthKey] || { to_produce: 0, fact: 0, plan: 0 };
      return sum + (data[type] || 0);
    }, 0);
  };

  // Calculate boxes
  const calculateBoxes = (totalPacks) => {
    return Math.ceil(totalPacks / 6000);
  };

  // Round values
  const roundValue = (value) => {
    const base = 1500;
    const remainder = value % base;
    if (remainder > 0 && remainder <= 99 && value > base) {
      return value - remainder;
    }
    return Math.ceil(value / base) * base;
  };

  const handleRoundValues = useCallback(async () => {
    try {
      setIsRounding(true);
      const updates = [];
      for (const row of tableData) {
        for (const monthKey of months) {
          const [year, month] = monthKey.split('-');
          const data = row.months[monthKey] || { to_produce: 0, fact: 0, plan: 0 };
          for (const type of ['to_produce']) {
            const originalValue = data[type];
            const roundedValue = roundValue(originalValue);
            if (roundedValue !== originalValue) {
              updates.push(
                api.put('/api/smallbags_forecast', {
                  forecast_id: selectedPlanning,
                  sbsku: row.sbsku,
                  year: parseInt(year),
                  month: parseInt(month),
                  [`total_${type}_quantity`]: roundedValue
                })
              );
            }
          }
        }
      }
      await Promise.all(updates);
      await fetchData();
      alert('Values rounded successfully!');
    } catch (error) {
      console.error('Rounding error:', error);
      alert('Error rounding values');
    } finally {
      setIsRounding(false);
    }
  }, [tableData, months, selectedPlanning]);

  useEffect(() => {
    if (onRoundRef) {
      onRoundRef(() => handleRoundValues);
    }
  }, [handleRoundValues, onRoundRef]);

  // Editing handlers
  const handleDoubleClick = (rowIndex, monthKey, type) => {
    setEditingCell({ rowIndex, monthKey, type });
  };

  const handleSave = async (sbsku, year, month, type, value) => {
    try {
      await api.put('/api/smallbags_forecast', {
        forecast_id: selectedPlanning,
        sbsku,
        year: parseInt(year),
        month: parseInt(month),
        [type]: parseInt(value) || 0
      });
      fetchData();
    } catch (error) {
      console.error('Error updating small bags data:', error);
    }
    setEditingCell(null);
  };

  // Since demand values are cumulative, we always subtract from the INITIAL stock.
  // If a month has no plan data, carry forward the previous month's Left (stock doesn't change if nothing is consumed).
  const calculateRollingLeft = (monthsData, sku, type) => {
    const initialSeedStock = Number(seedInventoryData[sku]) || 0;
    const initialStock = Number(inventoryData[sku]) || 0;
    const monthlyStock = {};
    let prevMonthStock = null;

    months.forEach((monthKey) => {
      if (!monthsData[monthKey]?.[type] && prevMonthStock !== null) {
        // Month not filled or filled with 0 — carry forward previous month's Left unchanged
        monthlyStock[monthKey] = prevMonthStock;
      } else {
        const demand = Number(monthsData[monthKey]?.[type]) || 0;

        const remainingSeedStock = Math.max(0, initialSeedStock - demand);
        const excessDemand = demand - (initialSeedStock - remainingSeedStock);
        const remainingStock = Number((initialStock - excessDemand).toFixed(2));

        monthlyStock[monthKey] = {
          seedStock: Number(remainingSeedStock.toFixed(2)),
          stock: Number(remainingStock.toFixed(2)),
          total: Number((remainingSeedStock + remainingStock).toFixed(2))
        };
      }

      prevMonthStock = monthlyStock[monthKey];
      console.log(`calculateRollingLeft: sku=${sku}, month=${monthKey}, type=${type}, seedStock=${monthlyStock[monthKey].seedStock}, stock=${monthlyStock[monthKey].stock}, total=${monthlyStock[monthKey].total}`);
    });

    return monthlyStock;
  };

  // Orders are summed cumulatively up to each month before subtracting demand.
  // If a month has no plan data but has new orders, recalculate with updated orders; otherwise carry forward.
  const calculateRollingLeftWithOrders = (monthsData, sku, type, ordersByMonth) => {
    const initialSeedStock = Number(seedInventoryData[sku]) || 0;
    const initialStock = Number(inventoryData[sku]) || 0;
    const monthlyStock = {};

    let cumulativeOrders = 0;
    let prevMonthStock = null;

    months.forEach((monthKey) => {
      // Orders are NOT cumulative in the source data, so we accumulate them month by month
      const ordersThisMonth = ordersByMonth[sku]?.[monthKey] || 0;
      cumulativeOrders += ordersThisMonth;

      if (!monthsData[monthKey]?.[type] && ordersThisMonth === 0 && prevMonthStock !== null) {
        // No plan (or plan is 0) and no new orders — carry forward previous month's Left
        monthlyStock[monthKey] = prevMonthStock;
      } else {
        const demand = Number(monthsData[monthKey]?.[type]) || 0;

        // Orders are for Empty Packets only — add to empty packet stock, not to Packets with Seeds
        const effectiveStock = initialStock + cumulativeOrders;
        const remainingSeedStock = Math.max(0, initialSeedStock - demand);
        const excessDemand = demand - (initialSeedStock - remainingSeedStock);
        const remainingStock = Number((effectiveStock - excessDemand).toFixed(2));

        monthlyStock[monthKey] = {
          seedStock: Number(remainingSeedStock.toFixed(2)),
          stock: Number(remainingStock.toFixed(2)),
          total: Number((remainingSeedStock + remainingStock).toFixed(2))
        };
      }

      prevMonthStock = monthlyStock[monthKey];
      console.log(`calculateRollingLeftWithOrders: sku=${sku}, month=${monthKey}, type=${type}, cumulativeOrders=${cumulativeOrders}, seedStock=${monthlyStock[monthKey].seedStock}, stock=${monthlyStock[monthKey].stock}, total=${monthlyStock[monthKey].total}`);
    });

    return monthlyStock;
  };

  const getLeftColor = (totalStock, monthlyData, currentMonthKey, type, sku) => {
    const currentDemand = Number(monthlyData[currentMonthKey]?.[sku]?.[type]) || 0;

    if (totalStock <= 0) return 'bg-red-600 text-white';
    if (totalStock < currentDemand) return 'bg-orange-500 text-white';
    if (totalStock >= currentDemand && totalStock < currentDemand * 1.5) return 'bg-yellow-500 text-white';
    return 'bg-green-600 text-white';
  };

  const compareOpSB = (val, op, threshold) => {
    switch (op) {
      case '>':  return val > threshold;
      case '>=': return val >= threshold;
      case '<':  return val < threshold;
      case '<=': return val <= threshold;
      case '=':  return val === threshold;
      default:   return true;
    }
  };

  // Pre-compute allMonthsData once (was O(N²) inside the render loop)
  const allMonthsData = useMemo(() =>
    tableData.reduce((acc, r) => {
      Object.keys(r.months).forEach(key => {
        acc[key] = acc[key] || {};
        acc[key][r.sbsku] = r.months[key];
      });
      return acc;
    }, {})
  , [tableData]);

  // Pre-compute rolling values per row once — shared by filter and render
  const rollingDataMap = useMemo(() => {
    const map = {};
    tableData.forEach(row => {
      map[row.sbsku] = {
        toProduceLeft:            calculateRollingLeft(row.months, row.sbsku, 'to_produce'),
        toProduceLeftWithOrders:  calculateRollingLeftWithOrders(row.months, row.sbsku, 'to_produce', ordersByEffectiveMonth),
      };
    });
    return map;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData, ordersByEffectiveMonth, months, inventoryData, seedInventoryData]);

  const filteredTableData = useMemo(() => {
    let data = tableData;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      data = data.filter(row =>
        row.sbsku?.toLowerCase().includes(q) ||
        row.packName?.toLowerCase().includes(q)
      );
    }
    const activeColFilters = Object.entries(columnFilters).filter(([, f]) => f?.value !== '');
    if (activeColFilters.length > 0) {
      data = data.filter(row => {
        const lwo = rollingDataMap[row.sbsku]?.toProduceLeftWithOrders || {};
        return activeColFilters.every(([mk, f]) =>
          compareOpSB(Number(lwo[mk]?.total) || 0, f.operator, parseFloat(f.value))
        );
      });
    }
    return data;
  }, [tableData, searchQuery, columnFilters, rollingDataMap]);

  if (!selectedPlanning) return <div>Please select a planning first</div>;

  return (
    <div className="table-wrapper">
      {isLoading ? (
        <div>Loading...</div>
      ) : tableData.length === 0 ? (
        <div>No small bags data available</div>
      ) : (
        <table className="table-planning">
          <thead>
            <tr className="bg-gray-100">
              <th rowSpan="2">№</th>
              <th rowSpan="2">Pack Name</th>
              <th rowSpan="2">Stock</th>
              {months.map(monthKey => {
                const [year, month] = monthKey.split('-');
                const monthName = monthNames[parseInt(month) - 1];
                const span = currentView === 'all' ? 4 : 1;
                return (
                  <th key={monthKey} colSpan={span}>
                    {`${monthName} ${year}`}
                  </th>
                );
              })}
            </tr>
            <tr className="bg-gray-50">
              {months.map(monthKey => (
                <React.Fragment key={monthKey}>
                  {currentView === 'all' && (
                    <>
                      <th>Demand</th>
                      <th>Left</th>
                      <th style={{ whiteSpace: 'nowrap' }}>
                        Left + Order
                        <ColumnFilterPopup
                          filter={columnFilters[monthKey]}
                          onChange={f => setColumnFilters(prev => ({ ...prev, [monthKey]: f }))}
                          onClear={() => setColumnFilters(prev => { const n = { ...prev }; delete n[monthKey]; return n; })}
                        />
                      </th>
                      <th>Order</th>
                    </>
                  )}
                  {currentView === 'left' && <th>Left</th>}
                  {currentView === 'left-order' && (
                    <th style={{ whiteSpace: 'nowrap' }}>
                      Left + Order
                      <ColumnFilterPopup
                        filter={columnFilters[monthKey]}
                        onChange={f => setColumnFilters(prev => ({ ...prev, [monthKey]: f }))}
                        onClear={() => setColumnFilters(prev => { const n = { ...prev }; delete n[monthKey]; return n; })}
                      />
                    </th>
                  )}
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredTableData.map((row, rowIndex) => {
              const stock = inventoryData[row.sbsku] || 0;
              const seedStock = seedInventoryData[row.sbsku] || 0;
              const rd = rollingDataMap[row.sbsku] || {};
              const rollingToProduceLeft           = rd.toProduceLeft           || {};
              const rollingToProduceLeftWithOrders = rd.toProduceLeftWithOrders || {};

              return (
                <tr key={row.sbsku}>
                  <td>{rowIndex + 1}</td>
                  <td>{`${row.brandID != null ? row.brandID : 'Unknown'} - ${row.packName || '-'}`}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                      <svg version="1.1" width="16" height="16" viewBox="0 0 480 480" style={{ marginRight: '4px', fill: '#6B7280' }}>
                        <path d="M407.944,7.704c-0.018-0.948-0.205-1.885-0.552-2.768c0-0.072-0.072-0.12-0.104-0.184c-0.214-0.502-0.482-0.98-0.8-1.424
                          c-0.201-0.248-0.418-0.483-0.648-0.704c-0.128-0.136-0.24-0.272-0.368-0.4c-0.643-0.612-1.384-1.112-2.192-1.48
                          c-0.16-0.067-0.322-0.126-0.488-0.176c-0.886-0.356-1.829-0.548-2.784-0.568h-320c-0.96,0.018-1.909,0.21-2.8,0.568
                          c-0.16,0.072-0.32,0.112-0.48,0.184c-0.808,0.368-1.548,0.868-2.192,1.48c-0.128,0.128-0.24,0.264-0.368,0.4
                          c-0.23,0.221-0.447,0.456-0.648,0.704c-0.316,0.445-0.584,0.922-0.8,1.424l-0.104,0.184c-0.347,0.883-0.534,1.82-0.552,2.768
                          c0,0.104-0.064,0.192-0.064,0.296v464c0,4.418,3.582,8,8,8h320.016c4.418,0,8-3.582,8-8V8
                          C408.008,7.896,407.952,7.808,407.944,7.704z M388.904,16L362.24,96h-91.368c-4.338-17.046-21.674-27.347-38.719-23.008
                          C220.848,75.869,212.021,84.696,209.144,96h-91.368L91.112,16H388.904z M256.008,104c0,8.837-7.163,16-16,16s-16-7.163-16-16
                          s7.163-16,16-16S256.008,95.163,256.008,104z M392.008,464h-304V57.288l16.416,49.24c1.088,3.266,4.142,5.469,7.584,5.472h97.136
                          c4.339,17.046,21.674,27.347,38.719,23.009c11.304-2.877,20.131-11.704,23.009-23.009h97.136c3.442-0.003,6.496-2.206,7.584-5.472
                          l16.416-49.24V464z"/>
                      </svg>
                      <span title="Empty Packets">{stock}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <svg version="1.1" width="16" height="16" viewBox="0 0 394.72 394.72" style={{ marginRight: '4px', fill: '#6B7280' }}>
                        <path d="M355.76,0h-206.4c-8.231,0.175-14.774,6.968-14.64,15.2v30.16H38.96c-8.231,0.175-14.774,6.968-14.64,15.2v318.96
                          c-0.134,8.232,6.409,15.025,14.64,15.2h206.4c8.231-0.175,14.774-6.968,14.64-15.2v-30.16h95.76
                          c8.231-0.175,14.774-6.968,14.64-15.2V15.2C370.534,6.968,363.991,0.175,355.76,0z M55.36,378.72l18.4-24h137.2l18.4,24H55.36z
                          M221.36,342.08c-1.51-1.962-3.844-3.114-6.32-3.12H69.84c-2.476,0.006-4.81,1.158-6.32,3.12l-23.2,29.76V126.4
                          c27.919,56.333,96.219,79.367,152.552,51.448c22.315-11.06,40.388-29.132,51.448-51.448l0.08,245.52L221.36,342.08z M142.4,173.36
                          c-57.04,0-102-49.12-102-112h204C244.4,124.24,199.52,173.36,142.4,173.36z M245.36,45.36C245.36,45.36,245.36,45.36,245.36,45.36
                          H153.6c-2.171-9.501-3.272-19.214-3.28-28.96h204c0.08,59.84-40.64,107.44-93.92,111.84v-68
                          C260.356,51.978,253.622,45.316,245.36,45.36z M260.4,333.36v-24h60.56l18.4,24H260.4z M354.4,326.56l-23.2-29.76
                          c-1.51-1.962-3.844-3.114-6.32-3.12H260.4V144.24c40.147-3.156,75.758-26.978,94-62.88V326.56z"/>
                        <circle cx="100" cy="100" r="20" fill="#6B7280"/>
                        <circle cx="140" cy="80" r="15" fill="#6B7280"/>
                        <circle cx="180" cy="100" r="18" fill="#6B7280"/>
                      </svg>
                      <span title="Packets with Seeds">{seedStock}</span>
                    </div>
                  </td>
                  {months.map(monthKey => {
                    const [year, month] = monthKey.split('-');
                    const data = row.months[monthKey] || { to_produce: 0, fact: 0, plan: 0 };
                    return (
                      <React.Fragment key={monthKey}>
                        {currentView === 'all' && (
                          <td className="demand-cell">
                            {filters?.selectedCalculations?.includes('Plan to Produce') && (
                              <div 
                                onDoubleClick={() => handleDoubleClick(rowIndex, monthKey, 'to_produce')} 
                                onClick={() => openDetailsPopup(row.sbsku, year, month)}
                                className="cursor-pointer demand-value"
                              >
                                {editingCell?.rowIndex === rowIndex &&
                                editingCell?.monthKey === monthKey &&
                                editingCell?.type === 'to_produce' ? (
                                  <input
                                    type="number"
                                    defaultValue={data.to_produce}
                                    onBlur={(e) => handleSave(row.sbsku, year, month, 'total_to_produce_quantity', e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && e.target.blur()}
                                  />
                                ) : (
                                  data.to_produce
                                )}
                              </div>
                            )}
                          </td>
                        )}
                        {(currentView === 'all' || currentView === 'left') && (
                          <td className="left-cell">
                            {filters?.selectedCalculations?.includes('Plan to Produce') && (
                              <div className="left-value">
                                <span className={getLeftColor(rollingToProduceLeft[monthKey]?.total || 0, allMonthsData, monthKey, 'to_produce', row.sbsku)} title="Packets with Seeds">
                                  {Number(rollingToProduceLeft[monthKey]?.seedStock || 0).toFixed(0)}
                                </span>
                                <span> | </span>
                                <span className={getLeftColor(rollingToProduceLeft[monthKey]?.total || 0, allMonthsData, monthKey, 'to_produce', row.sbsku)} title="Empty Packets">
                                  {Number(rollingToProduceLeft[monthKey]?.stock || 0).toFixed(0)}
                                </span>
                              </div>
                            )}
                          </td>
                        )}
                        {(currentView === 'all' || currentView === 'left-order') && (
                          <td className="left-plus-order-cell">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                {filters?.selectedCalculations?.includes('Plan to Produce') && (
                                  <div className="left-value">
                                    <span className={getLeftColor(rollingToProduceLeftWithOrders[monthKey]?.total || 0, allMonthsData, monthKey, 'to_produce', row.sbsku)} title="Packets with Seeds">
                                      {Number(rollingToProduceLeftWithOrders[monthKey]?.seedStock || 0).toFixed(0)}
                                    </span>
                                    <span> | </span>
                                    <span className={getLeftColor(rollingToProduceLeftWithOrders[monthKey]?.total || 0, allMonthsData, monthKey, 'to_produce', row.sbsku)} title="Empty Packets">
                                      {Number(rollingToProduceLeftWithOrders[monthKey]?.stock || 0).toFixed(0)}
                                    </span>
                                  </div>
                                )}
                              </div>
                              {currentView === 'left-order' && (
                                <button
                                  className="planning-btn-add-order"
                                  onClick={() => openAddOrderPopup(row.sbsku, year, parseInt(month), row.packName)}
                                  style={{ marginLeft: '8px' }}
                                >
                                  +
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                        {currentView === 'all' && (
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                              {(ordersByEffectiveMonthList[row.sbsku]?.[monthKey] || []).map((ord, i) => {
                                const accent = { S: '#16a34a', OP: '#059669', BO: '#ea580c', C: '#6b7280' }[ord.statusID] || '#6b7280';
                                return (
                                  <div key={ord.id ?? i}
                                    className="text-xs cursor-pointer"
                                    onClick={() => openAddOrderPopup(row.sbsku, year, parseInt(month), row.packName, ord)}
                                    style={{ borderLeft: `3px solid ${accent}`, background: '#f8fafc', borderRadius: '3px', padding: '2px 5px', lineHeight: '1.4' }}>
                                    <div style={{ fontWeight: 600 }}>{ord.qty}</div>
                                    <div style={{ color: accent }}>{ord.statusID}</div>
                                  </div>
                                );
                              })}
                              <button
                                className="planning-btn-add-order"
                                onClick={() => openAddOrderPopup(row.sbsku, year, parseInt(month), row.packName)}
                              >
                                +
                              </button>
                            </div>
                          </td>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tr>
              );
            })}
            {currentView === 'all' && (
              <tr className="total-row">
                <td colSpan="2">Total Packs</td>
                <td>-</td>
                {months.map(monthKey => (
                  <React.Fragment key={monthKey}>
                    <td className="demand-cell">
                      <div className="demand-value">{calculateTotalPacks('to_produce', monthKey)}</div>
                    </td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                  </React.Fragment>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      )}
      {isUploadOrdersOpen && (
        <UploadOrdersPopup_SmallBags
          suppliers={suppliers}
          onClose={onUploadOrdersClose}
          onSuccess={async () => { onUploadOrdersClose(); await fetchData(); await fetchOrdersData(); }}
        />
      )}
      {isAddOrderOpen && (
        <AddOrderPopup_SmallBags
          sku={currentOrderData.sku}
          year={currentOrderData.year}
          month_num={currentOrderData.month_num}
          setName={currentOrderData.packName}
          statuses={statuses}
          suppliers={suppliers}
          materialtypeID={1}
          onClose={closeAddOrderPopup}
          onSave={handleOrderSave}
          initialData={currentOrderData.initialData}
        />
      )}
      {isDetailsOpen && (
        <DemandDetailsPopup
          materialType="smallbags"
          sku={detailsData.sku}
          year={detailsData.year}
          month={detailsData.month}
          type={detailsData.type}
          forecastId={selectedPlanning}
          onClose={closeDetailsPopup}
        />
      )}
    </div>
  );
};

export default SmallBagsTab;